import { ZodError, type ZodIssue, type ZodType } from "zod";

// Re-exported so browser consumers (apps/web) can type schema params without a
// direct "zod" module specifier they can't resolve from their own package.
export type { ZodType } from "zod";

/**
 * Field-level validation results, shared by the API (request validation) and
 * the web app (submit-time form validation) so a rejected field looks the same
 * on both sides.
 *
 * A zod issue is verbose and English-only. We flatten it into a small, stable
 * `FieldIssue` — a `field` path, a coarse `rule`, and its `params` — that the
 * panel maps to a localized message (see apps/web/src/errors/catalog.ts). The
 * original zod `message` rides along as an English fallback.
 */

export type FieldRule =
  | "required" // value missing / undefined
  | "too_short" // string/array below min length
  | "too_long" // string/array above max length
  | "min" // number below minimum
  | "max" // number above maximum
  | "not_int" // number expected to be an integer
  | "invalid_type" // wrong primitive type (not the "missing" case)
  | "email"
  | "url"
  | "uuid"
  | "regex"
  | "enum"
  | "date"
  | "custom";

export interface FieldIssueParams {
  /** Min length (string/array) or minimum value (number), when applicable. */
  min?: number;
  /** Max length (string/array) or maximum value (number), when applicable. */
  max?: number;
  /** Allowed values for an enum rule. */
  options?: readonly string[];
}

export interface FieldIssue {
  /** Dot-joined path, e.g. "slug" or "attrs.identity.gtin". "" = form-level. */
  field: string;
  rule: FieldRule;
  params: FieldIssueParams;
  /** English fallback (zod's message) — used only if the client has no mapping. */
  message: string;
}

/**
 * A missing field. zod 4 omits the offending input from issues unless parsing
 * opts into `reportInput` (which we avoid: it would copy raw values such as
 * passwords into error objects), so fall back to its standard message.
 */
function isMissing(issue: ZodIssue): boolean {
  if ("input" in issue) return issue.input === undefined;
  return /received undefined$/.test(issue.message);
}

function ruleFor(issue: ZodIssue): { rule: FieldRule; params: FieldIssueParams } {
  switch (issue.code) {
    case "invalid_type":
      // A missing field is its own rule so the client can say "required"
      // rather than "wrong type". `.int()` on a fraction is also invalid_type.
      if (isMissing(issue)) return { rule: "required", params: {} };
      if (issue.expected === "int" || issue.expected === "integer") return { rule: "not_int", params: {} };
      if (issue.expected === "date") return { rule: "date", params: {} };
      return { rule: "invalid_type", params: {} };

    case "too_small":
      // string/array carry a length; number carries a value floor.
      return issue.origin === "number" || issue.origin === "bigint"
        ? { rule: "min", params: { min: Number(issue.minimum) } }
        : { rule: "too_short", params: { min: Number(issue.minimum) } };

    case "too_big":
      return issue.origin === "number" || issue.origin === "bigint"
        ? { rule: "max", params: { max: Number(issue.maximum) } }
        : { rule: "too_long", params: { max: Number(issue.maximum) } };

    case "invalid_format": {
      const f = issue.format;
      if (f === "email") return { rule: "email", params: {} };
      if (f === "url") return { rule: "url", params: {} };
      if (f === "uuid" || f === "guid") return { rule: "uuid", params: {} };
      if (f === "regex") return { rule: "regex", params: {} };
      if (f === "datetime" || f === "date") return { rule: "date", params: {} };
      // cuid / other string formats — no dedicated message, treat generic.
      return { rule: "custom", params: {} };
    }

    case "invalid_value":
      // enum / literal mismatch
      return { rule: "enum", params: { options: issue.values.map(String) } };

    default:
      // not_multiple_of, unrecognized_keys, custom refinements, unions, …
      return { rule: "custom", params: {} };
  }
}

/** Flatten a ZodError into stable field issues. */
export function toFieldIssues(err: ZodError): FieldIssue[] {
  return err.issues.map((issue) => {
    const { rule, params } = ruleFor(issue);
    return { field: issue.path.map(String).join("."), rule, params, message: issue.message };
  });
}

export type ValidationResult<T> = { ok: true; data: T } | { ok: false; issues: FieldIssue[] };

/**
 * Parse `value` against `schema`, returning either the typed data or the field
 * issues. The single validation entry point used by both API services and the
 * panel's form hook, so the two never diverge on what "valid" means.
 */
export function validateInput<T>(schema: ZodType<T>, value: unknown): ValidationResult<T> {
  const parsed = schema.safeParse(value);
  if (parsed.success) return { ok: true, data: parsed.data };
  return { ok: false, issues: toFieldIssues(parsed.error) };
}

/** Narrowing helper for `unknown` catch bindings. */
export function isZodError(e: unknown): e is ZodError {
  return e instanceof ZodError;
}
